import { Link } from 'react-router-dom';

export interface Benefit {
  icon: string;
  title: string;
  description: string;
}

export interface WhyChooseUsProps {
  title: string;
  subtitle: string;
  benefits: Benefit[];
  image: string;
  image2?: string;
  ctaLabel?: string;
  ctaHref?: string;
  badge?: string;
  badgeTitle?: string;
  badgeSubtext?: string;
}

export default function WhyChooseUs({
  title,
  subtitle,
  benefits,
  image,
  image2,
  ctaLabel = 'Get Free Estimate',
  ctaHref = '/#quote',
  badge = 'Why Choose Us',
  badgeTitle = '25+ Years',
  badgeSubtext = 'In Brooklyn',
}: WhyChooseUsProps) {
  return (
    <section className="bg-ivory py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          <div className="relative">
            <div className="w-full h-96 lg:h-[520px] rounded-2xl overflow-hidden">
              <img
                src={image}
                alt="Premium flooring installation specialists"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {image2 && (
              <div className="absolute -bottom-6 -right-6 w-48 h-36 rounded-xl overflow-hidden border-4 border-white hidden lg:block">
                <img
                  src={image2}
                  alt="Flooring material close-up detail"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}
            {/* Badge */}
            <div className="absolute top-6 left-6 bg-white rounded-xl px-4 py-3 flex items-center gap-2.5 border border-warmgray">
              <div className="w-8 h-8 flex items-center justify-center bg-bronze/10 rounded-full">
                <i className="ri-award-line text-bronze"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-charcoal">{badgeTitle}</p>
                <p className="text-xs text-gray-500">{badgeSubtext}</p>
              </div>
            </div>
          </div>

          {/* Content side */}
          <div>
            <p className="text-bronze text-xs font-semibold uppercase tracking-widest mb-3">{badge}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-charcoal leading-tight mb-3">
              {title}
            </h2>
            <p className="text-gray-500 mb-10 leading-relaxed">{subtitle}</p>

            <div className="space-y-6">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-bronze/10 rounded-lg shrink-0 mt-0.5">
                    <i className={`${b.icon} text-bronze text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal text-base mb-1">{b.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 mt-10 bg-bronze text-white font-semibold px-8 py-4 rounded-md hover:bg-bronze-dark transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {ctaLabel}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
